/**
 * Dynamic field configuration system for different section types
 */

// Field configurations for different section types
const FIELD_CONFIGS = {
  userRoles: [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter role name (e.g., Administrator, Support Agent)'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      rows: 3,
      placeholder: 'Describe the role\'s primary purpose, responsibilities, and scope within the system'
    },
    {
      name: 'permissionLevel',
      label: 'Permission level',
      type: 'select',
      required: true,
      options: [
        { value: 'Full Access', label: 'Full Access' },
        { value: 'Limited Access', label: 'Limited Access' },
        { value: 'Read-Only', label: 'Read-Only' },
        { value: 'Custom', label: 'Custom' }
      ]
    },
    {
      name: 'accessScope',
      label: 'Access scope',
      type: 'textarea',
      required: true,
      rows: 3,
      placeholder: 'List modules/features this role can access (one per line):\n- User Management Module\n- Analytics & Reporting\n- Content Management'
    },
    {
      name: 'keyResponsibilities',
      label: 'Key responsibilities',
      type: 'textarea',
      required: true,
      rows: 3,
      placeholder: 'List core duties and accountabilities (one per line):\n- Manage user accounts and permissions\n- Monitor system health and performance'
    },
    {
      name: 'mainWorkflows',
      label: 'Main workflows',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'List primary tasks and processes (one per line):\n- User account creation and role assignment\n- System configuration and maintenance'
    },
    {
      name: 'reportsTo',
      label: 'Reports to',
      type: 'text',
      required: false,
      placeholder: 'Direct supervisor role (e.g., Platform Administrator) or "None"'
    },
    {
      name: 'manages',
      label: 'Manages',
      type: 'text',
      required: false,
      placeholder: 'Roles this position oversees (e.g., Content Manager, Support Agent) or "None"'
    },
    {
      name: 'restrictions',
      label: 'Restrictions',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'What this role explicitly CANNOT do (one per line):\n- Cannot delete audit logs\n- Cannot access financial reports'
    },
    {
      name: 'keyPermissions',
      label: 'Key permissions',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'Permission codes for development (one per line):\n- user:create\n- user:read\n- settings:manage'
    }
  ],

  userStories: [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter user story title'
    },
    {
      name: 'actors',
      label: 'Actor(s)',
      type: 'text',
      required: false,
      placeholder: 'Enter actor(s) (e.g., End User, Admin, System)'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      rows: 3,
      placeholder: 'Describe what the user wants to accomplish and why'
    },
    {
      name: 'trigger',
      label: 'Trigger',
      type: 'text',
      required: false,
      placeholder: 'What initiates this user story (e.g., User clicks login button)'
    },
    {
      name: 'preconditions',
      label: 'Preconditions',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'Enter preconditions (one per line):\n- User must be logged in\n- User has valid permissions'
    },
    {
      name: 'postconditions',
      label: 'Postconditions',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'Enter postconditions (one per line):\n- User session is created\n- Dashboard is displayed'
    },
    {
      name: 'normalFlow',
      label: 'Normal Flow',
      type: 'textarea',
      required: false,
      rows: 4,
      placeholder: 'Enter normal flow steps (one per line):\n1. User navigates to login page\n2. User enters credentials\n3. System validates credentials'
    },
    {
      name: 'alternativeFlows',
      label: 'Alternative Flows & Exceptions',
      type: 'textarea',
      required: false,
      rows: 4,
      placeholder: 'Enter alternative flows & exceptions (one per line):\n- If credentials invalid, show error message\n- If account locked, redirect to support'
    },
    {
      name: 'acceptanceCriteria',
      label: 'Acceptance Criteria',
      type: 'textarea',
      required: false,
      rows: 4,
      placeholder: 'Enter acceptance criteria (one per line):\n- The user should be able to...\n- The system must...'
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      required: false,
      options: [
        { value: 'must-have', label: 'Must have' },
        { value: 'should-have', label: 'Should have' },
        { value: 'could-have', label: 'Could have' },
        { value: 'wont-have', label: "Won't have" }
      ]
    },
    {
      name: 'relatedFeature',
      label: 'Related Feature',
      type: 'feature-select',
      required: false,
      placeholder: 'Select or add a feature'
    }
  ],

  'test-section': [
    {
      name: 'title',
      label: 'Test Section Title',
      type: 'text',
      required: true,
      placeholder: 'e.g., LoginComponent Tests, Authentication API Tests'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'Brief description of what this test covers...'
    },
    {
      name: 'code',
      label: 'Test Code',
      type: 'textarea',
      required: true,
      rows: 15,
      placeholder: `describe('ComponentName', () => {
  test('should render correctly', () => {
    // Arrange
    const props = { ... };

    // Act
    render(<Component {...props} />);

    // Assert
    expect(screen.getByText('...')).toBeInTheDocument();
  });

  test('should handle user interaction', () => {
    // Test implementation
  });
});`,
      helperText: 'Write your test code using Jest, React Testing Library, or your testing framework',
      isMonospace: true
    }
  ],

  'security-test-section': [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'e.g., SQL Injection Tests, XSS Prevention, Authentication Security'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      rows: 3,
      placeholder: 'Brief description of the security test objective...'
    },
    {
      name: 'contentType',
      label: 'Content Type',
      type: 'select',
      required: true,
      options: [
        { value: 'code', label: 'Code Block (Programming Language)' },
        { value: 'tree', label: 'Tree Structure (Diagram/Framework)' }
      ]
    },
    {
      name: 'language',
      label: 'Programming Language',
      type: 'select',
      required: false,
      showIf: { field: 'contentType', value: 'code' },
      options: [
        { value: 'javascript', label: 'JavaScript' },
        { value: 'typescript', label: 'TypeScript' },
        { value: 'python', label: 'Python' },
        { value: 'java', label: 'Java' },
        { value: 'csharp', label: 'C#' },
        { value: 'bash', label: 'Bash/Shell' },
        { value: 'sql', label: 'SQL' },
        { value: 'yaml', label: 'YAML' },
        { value: 'json', label: 'JSON' }
      ]
    },
    {
      name: 'content',
      label: 'Content',
      type: 'textarea',
      required: true,
      rows: 20,
      placeholder: 'Enter your security test content...',
      helperText: 'For Code Block: Enter your test code. For Tree Structure: Enter structured text/diagram',
      isMonospace: true
    }
  ],

  'performance-test-section': [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'e.g., Load Testing, Response Time Benchmarks, Memory Profiling'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      rows: 3,
      placeholder: 'Brief description of the performance test objective...'
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      required: false,
      options: [
        { value: 'Load Testing', label: 'Load Testing' },
        { value: 'Stress Testing', label: 'Stress Testing' },
        { value: 'Scalability Testing', label: 'Scalability Testing' },
        { value: 'Endurance Testing', label: 'Endurance Testing' },
        { value: 'Spike Testing', label: 'Spike Testing' },
        { value: 'Volume Testing', label: 'Volume Testing' },
        { value: 'Custom Tests', label: 'Custom Tests' }
      ]
    },
    {
      name: 'contentType',
      label: 'Content Type',
      type: 'select',
      required: true,
      options: [
        { value: 'code', label: 'Code Block (Test Script)' },
        { value: 'metrics', label: 'Metrics/Benchmarks' },
        { value: 'config', label: 'Configuration' }
      ]
    },
    {
      name: 'language',
      label: 'Programming Language',
      type: 'select',
      required: false,
      showIf: { field: 'contentType', value: 'code' },
      options: [
        { value: 'javascript', label: 'JavaScript' },
        { value: 'typescript', label: 'TypeScript' },
        { value: 'python', label: 'Python' },
        { value: 'java', label: 'Java' },
        { value: 'bash', label: 'Bash/Shell' },
        { value: 'yaml', label: 'YAML' },
        { value: 'json', label: 'JSON' }
      ]
    },
    {
      name: 'content',
      label: 'Content',
      type: 'textarea',
      required: true,
      rows: 20,
      placeholder: 'Enter your performance test content...',
      helperText: 'For Code Block: Enter your test script. For Metrics: Enter benchmark data. For Config: Enter test configuration',
      isMonospace: true
    }
  ],

  'unit-test': [
    {
      name: 'title',
      label: 'Test Section Title',
      type: 'text',
      required: true,
      placeholder: 'e.g., LoginComponent Tests, Authentication API Tests'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'Brief description of what this test covers...'
    },
    {
      name: 'code',
      label: 'Test Code',
      type: 'textarea',
      required: false,
      rows: 15,
      placeholder: `describe('ComponentName', () => {
  test('should render correctly', () => {
    // Arrange
    const props = { ... };

    // Act
    render(<Component {...props} />);

    // Assert
    expect(screen.getByText('...')).toBeInTheDocument();
  });

  test('should handle user interaction', () => {
    // Test implementation
  });
});`,
      helperText: 'Write your test code using Jest, React Testing Library, or your testing framework',
      isMonospace: true
    }
  ],

  'functional-test-section': [
    {
      name: 'title',
      label: 'Test Section Title',
      type: 'text',
      required: true,
      placeholder: 'e.g., Login Flow Test, User Registration Test, Payment Processing Test'
    },
    {
      name: 'description',
      label: 'Test Description',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'Brief description of what this functional test validates...'
    },
    {
      name: 'testSteps',
      label: 'Test Steps & Expected Results',
      type: 'textarea',
      required: false,
      rows: 15,
      placeholder: `**Test ID**: TC-UF-001
**Priority**: P0
**Estimated Time**: 5 minutes

**Prerequisites**:
- User account exists in the system
- User is logged out

**Test Steps**:
1. Navigate to the login page
2. Enter valid email address
3. Enter valid password
4. Click the "Login" button

**Expected Results**:
- User is redirected to the dashboard
- Welcome message displays user's name
- Session cookie is created
- Last login timestamp is updated`,
      helperText: 'Include test ID, priority, prerequisites, detailed steps, and expected results',
      isMonospace: true
    }
  ],

  'automated-test-section': [
    {
      name: 'title',
      label: 'Automation Test Title',
      type: 'text',
      required: true,
      placeholder: 'e.g., Login Page Object Model, API Authentication Tests, CI/CD Pipeline Setup'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'Brief description of the automation test framework, strategy, or implementation...'
    },
    {
      name: 'framework',
      label: 'Framework/Tool',
      type: 'text',
      required: false,
      placeholder: 'e.g., Playwright, Selenium, RestAssured, Cypress, Postman'
    },
    {
      name: 'language',
      label: 'Programming Language',
      type: 'select',
      required: true,
      options: [
        { value: 'javascript', label: 'JavaScript' },
        { value: 'typescript', label: 'TypeScript' },
        { value: 'python', label: 'Python' },
        { value: 'java', label: 'Java' },
        { value: 'csharp', label: 'C#' },
        { value: 'yaml', label: 'YAML' },
        { value: 'gherkin', label: 'Gherkin/Cucumber' }
      ]
    },
    {
      name: 'code',
      label: 'Automation Code',
      type: 'textarea',
      required: true,
      rows: 20,
      placeholder: `// Example: Page Object Model for Login
class LoginPage {
  constructor(page) {
    this.page = page;
    this.emailInput = '#email';
    this.passwordInput = '#password';
    this.loginButton = 'button[type="submit"]';
  }

  async login(email, password) {
    await this.page.fill(this.emailInput, email);
    await this.page.fill(this.passwordInput, password);
    await this.page.click(this.loginButton);
  }

  async isLoggedIn() {
    return await this.page.isVisible('.user-profile');
  }
}

module.exports = LoginPage;`,
      helperText: 'Write automation code using your testing framework (Playwright, Selenium, Cypress, etc.)',
      isMonospace: true
    }
  ],

  'git-guideline': [
    {
      name: 'title',
      label: 'Section Title',
      type: 'text',
      required: true,
      placeholder: 'e.g., Code Organization and Structure, Testing Guidelines, Branching Strategy'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      rows: 4,
      placeholder: 'Describe this guideline section in detail...'
    },
    {
      name: 'codeType',
      label: 'Code Type',
      type: 'select',
      required: false,
      options: [
        { value: 'none', label: 'None' },
        { value: 'mermaid', label: 'Mermaid Diagram' },
        { value: 'yaml', label: 'YAML' },
        { value: 'json', label: 'JSON' },
        { value: 'table', label: 'Markdown Table' },
        { value: 'tree', label: 'Tree Structure' },
        { value: 'typescript', label: 'TypeScript' },
        { value: 'javascript', label: 'JavaScript' },
        { value: 'bash', label: 'Bash/Shell' }
      ]
    },
    {
      name: 'mermaidCode',
      label: 'Mermaid Diagram Code',
      type: 'textarea',
      required: false,
      rows: 10,
      placeholder: 'graph TD\n    A[Feature Branch] --> B[Pull Request]\n    B --> C[Code Review]\n    C --> D[Merge to Main]\n    D --> E[Deploy]',
      helperText: 'Add Mermaid diagram code to visualize workflows or processes',
      isMonospace: true,
      showIf: { field: 'codeType', value: 'mermaid' }
    },
    {
      name: 'yamlCode',
      label: 'YAML Configuration',
      type: 'textarea',
      required: false,
      rows: 12,
      placeholder: '# Git configuration example\nbranches:\n  main:\n    protected: true\n    require-reviews: 2\n  develop:\n    protected: true\n    require-reviews: 1',
      helperText: 'Add YAML configuration or rules',
      isMonospace: true,
      showIf: { field: 'codeType', value: 'yaml' }
    },
    {
      name: 'jsonCode',
      label: 'JSON Configuration',
      type: 'textarea',
      required: false,
      rows: 12,
      placeholder: '{\n  "commitlint": {\n    "extends": ["@commitlint/config-conventional"],\n    "rules": {\n      "type-enum": [2, "always", ["feat", "fix", "docs", "style", "refactor"]]\n    }\n  }\n}',
      helperText: 'Add JSON configuration or data structure',
      isMonospace: true,
      showIf: { field: 'codeType', value: 'json' }
    },
    {
      name: 'tableCode',
      label: 'Markdown Table',
      type: 'textarea',
      required: false,
      rows: 10,
      placeholder: '| Branch Type | Naming Convention | Merge Target | Approvals |\n|-------------|-------------------|--------------|----------|\n| Feature | feature/TICKET-description | develop | 1 |\n| Bugfix | bugfix/TICKET-description | develop | 1 |\n| Hotfix | hotfix/TICKET-description | main | 2 |',
      helperText: 'Add a markdown table to document guidelines',
      isMonospace: true,
      showIf: { field: 'codeType', value: 'table' }
    },
    {
      name: 'treeCode',
      label: 'Tree Structure',
      type: 'textarea',
      required: false,
      rows: 15,
      placeholder: 'repository/\n├── src/\n│   ├── components/\n│   │   ├── common/\n│   │   └── features/\n│   ├── hooks/\n│   ├── utils/\n│   └── services/\n├── tests/\n│   ├── unit/\n│   └── integration/\n├── docs/\n└── scripts/',
      helperText: 'Use spaces and special characters (├── └──) to create tree structures',
      isMonospace: true,
      showIf: { field: 'codeType', value: 'tree' }
    },
    {
      name: 'typescriptCode',
      label: 'TypeScript Code',
      type: 'textarea',
      required: false,
      rows: 12,
      placeholder: '// Example: Commit message type definitions\ntype CommitType = "feat" | "fix" | "docs" | "style" | "refactor" | "test" | "chore";\n\ninterface CommitMessage {\n  type: CommitType;\n  scope?: string;\n  subject: string;\n  body?: string;\n  breaking?: boolean;\n}',
      helperText: 'Add TypeScript code examples or type definitions',
      isMonospace: true,
      showIf: { field: 'codeType', value: 'typescript' }
    },
    {
      name: 'javascriptCode',
      label: 'JavaScript Code',
      type: 'textarea',
      required: false,
      rows: 12,
      placeholder: '// Example: Pre-commit hook\nmodule.exports = {\n  hooks: {\n    "pre-commit": "lint-staged",\n    "commit-msg": "commitlint -E HUSKY_GIT_PARAMS"\n  }\n};',
      helperText: 'Add JavaScript code examples or configurations',
      isMonospace: true,
      showIf: { field: 'codeType', value: 'javascript' }
    },
    {
      name: 'bashCode',
      label: 'Bash/Shell Script',
      type: 'textarea',
      required: false,
      rows: 12,
      placeholder: '#!/bin/bash\n# Example: Git workflow script\n\n# Create feature branch\ngit checkout develop\ngit pull origin develop\ngit checkout -b feature/TICKET-description\n\n# Push and create PR\ngit push -u origin feature/TICKET-description',
      helperText: 'Add shell scripts or command examples',
      isMonospace: true,
      showIf: { field: 'codeType', value: 'bash' }
    }
  ],

  // Default configuration for generic sections
  default: [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter title'
    },
    {
      name: 'content',
      label: 'Content',
      type: 'textarea',
      required: true,
      rows: 6,
      placeholder: 'Enter content'
    }
  ]
};

// pipeline-stage uses the same config as git-guideline
FIELD_CONFIGS['pipeline-stage'] = FIELD_CONFIGS['git-guideline'];

/**
 * Get field configuration for a specific section type
 * @param {string} sectionType - The type of section (e.g., 'userRoles', 'features')
 * @returns {Array} Array of field configurations
 */
export const getFieldConfig = (sectionType = 'default') => {
  return FIELD_CONFIGS[sectionType] || FIELD_CONFIGS.default;
};

/**
 * Legacy function for backward compatibility
 */
export const getRoleFields = () => {
  return getFieldConfig('userRoles');
};

/**
 * Content generation templates for different section types
 */
const CONTENT_GENERATORS = {
  userRoles: (formData) => {
    const sections = [];

    // Helper to format text as bullet list if multiline
    const formatAsList = (text) => {
      const trimmed = text.trim();
      if (trimmed.includes('\n') && !trimmed.match(/^[-•*]\s/m)) {
        const lines = trimmed.split('\n').filter(line => line.trim());
        return lines.map(line => `- ${line.trim().replace(/^[-•*]\s*/, '')}`).join('\n');
      }
      return trimmed;
    };

    if (formData.description) {
      sections.push(`**Description:** ${formData.description}`);
    }

    if (formData.permissionLevel) {
      sections.push(`**Permission Level:** ${formData.permissionLevel}`);
    }

    if (formData.accessScope) {
      sections.push(`**Access Scope:**\n${formatAsList(formData.accessScope)}`);
    }

    if (formData.keyResponsibilities) {
      sections.push(`**Key Responsibilities:**\n${formatAsList(formData.keyResponsibilities)}`);
    }

    if (formData.mainWorkflows) {
      sections.push(`**Main Workflows:**\n${formatAsList(formData.mainWorkflows)}`);
    }

    if (formData.reportsTo) {
      sections.push(`**Reports To:** ${formData.reportsTo}`);
    }

    if (formData.manages) {
      sections.push(`**Manages:** ${formData.manages}`);
    }

    if (formData.restrictions) {
      sections.push(`**Restrictions:**\n${formatAsList(formData.restrictions)}`);
    }

    if (formData.keyPermissions) {
      sections.push(`**Key Permissions:**\n${formatAsList(formData.keyPermissions)}`);
    }

    return sections.join('\n\n');
  },

  userStories: (formData) => {
    const sections = [];

    // Helper function to format list items (bullet or numbered)
    const formatAsBulletList = (text) => {
      const trimmed = text.trim();
      if (trimmed.includes('\n') && !trimmed.match(/^[-•*]\s/m)) {
        const lines = trimmed.split('\n').filter(line => line.trim());
        return lines.map(line => `- ${line.trim()}`).join('\n');
      } else if (trimmed.match(/^[-•*]\s/m)) {
        return trimmed;
      } else {
        return `- ${trimmed}`;
      }
    };

    const formatAsNumberedList = (text) => {
      const trimmed = text.trim();
      if (trimmed.includes('\n') && !trimmed.match(/^\d+\.\s/m)) {
        const lines = trimmed.split('\n').filter(line => line.trim());
        return lines.map((line, idx) => `${idx + 1}. ${line.trim()}`).join('\n');
      } else if (trimmed.match(/^\d+\.\s/m)) {
        return trimmed;
      } else {
        return `1. ${trimmed}`;
      }
    };

    // Add Actor(s)
    if (formData.actors) {
      sections.push(`**Actor(s)**: ${formData.actors}`);
    }

    // Add Description
    if (formData.description) {
      sections.push(`**Description**: ${formData.description}`);
    }

    // Add Trigger
    if (formData.trigger) {
      sections.push(`**Trigger**: ${formData.trigger}`);
    }

    // Add Preconditions
    if (formData.preconditions) {
      sections.push(`**Preconditions**:\n${formatAsBulletList(formData.preconditions)}`);
    }

    // Add Postconditions
    if (formData.postconditions) {
      sections.push(`**Postconditions**:\n${formatAsBulletList(formData.postconditions)}`);
    }

    // Add Normal Flow
    if (formData.normalFlow) {
      sections.push(`**Normal Flow**:\n${formatAsNumberedList(formData.normalFlow)}`);
    }

    // Add Alternative Flows & Exceptions
    if (formData.alternativeFlows) {
      sections.push(`**Alternative Flows & Exceptions**:\n${formatAsBulletList(formData.alternativeFlows)}`);
    }

    // Add Acceptance Criteria
    if (formData.acceptanceCriteria) {
      sections.push(`**Acceptance Criteria**:\n${formatAsBulletList(formData.acceptanceCriteria)}`);
    }

    // Add Priority
    if (formData.priority) {
      // Format the priority value nicely
      const priorityLabel = formData.priority.split('-').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      sections.push(`**Priority**: ${priorityLabel}`);
    }

    // Add Related Feature
    if (formData.relatedFeature) {
      sections.push(`**Related Feature**: ${formData.relatedFeature}`);
    }

    return sections.join('\n\n');
  },

  'test-section': (formData) => {
    const contentParts = [];

    if (formData.description?.trim()) {
      contentParts.push(formData.description.trim());
    }

    if (formData.code?.trim()) {
      contentParts.push('\n```javascript\n' + formData.code.trim() + '\n```');
    }

    return contentParts.join('\n\n');
  },

  'security-test-section': (formData) => {
    const contentParts = [];

    if (formData.description?.trim()) {
      contentParts.push(formData.description.trim());
    }

    if (formData.content?.trim()) {
      const contentType = formData.contentType || 'code';

      if (contentType === 'code') {
        // Code block with specified programming language
        const language = formData.language || 'javascript';
        contentParts.push('\n```' + language + '\n' + formData.content.trim() + '\n```');
      } else if (contentType === 'tree') {
        // Tree structure/diagram as plain text code block for better formatting
        contentParts.push('\n```text\n' + formData.content.trim() + '\n```');
      }
    }

    return contentParts.join('\n\n');
  },

  'performance-test-section': (formData) => {
    const contentParts = [];

    if (formData.description?.trim()) {
      contentParts.push(formData.description.trim());
    }

    if (formData.content?.trim()) {
      const contentType = formData.contentType || 'code';

      if (contentType === 'code') {
        // Code block with specified programming language
        const language = formData.language || 'javascript';
        contentParts.push('\n```' + language + '\n' + formData.content.trim() + '\n```');
      } else if (contentType === 'metrics') {
        // Metrics/benchmarks as text block for better formatting
        contentParts.push('\n```text\n' + formData.content.trim() + '\n```');
      } else if (contentType === 'config') {
        // Configuration as YAML/JSON code block
        contentParts.push('\n```yaml\n' + formData.content.trim() + '\n```');
      }
    }

    return contentParts.join('\n\n');
  },

  'unit-test': (formData) => {
    const contentParts = [];

    if (formData.description?.trim()) {
      contentParts.push(formData.description.trim());
    }

    if (formData.code?.trim()) {
      contentParts.push('\n```javascript\n' + formData.code.trim() + '\n```');
    }

    return contentParts.join('\n\n');
  },

  'functional-test-section': (formData) => {
    const contentParts = [];

    if (formData.description?.trim()) {
      contentParts.push(formData.description.trim());
    }

    if (formData.testSteps?.trim()) {
      contentParts.push(formData.testSteps.trim());
    }

    return contentParts.join('\n\n');
  },

  'automated-test-section': (formData) => {
    const contentParts = [];

    if (formData.description?.trim()) {
      contentParts.push(formData.description.trim());
    }

    if (formData.framework?.trim()) {
      contentParts.push(`**Framework/Tool:** ${formData.framework.trim()}`);
    }

    if (formData.code?.trim()) {
      // Use the selected language or default to javascript
      const language = formData.language || 'javascript';
      contentParts.push('\n```' + language + '\n' + formData.code.trim() + '\n```');
    }

    return contentParts.join('\n\n');
  },

  'git-guideline': (formData) => {
    const contentParts = [];

    if (formData.description?.trim()) {
      contentParts.push(formData.description.trim());
    }

    // Add code block based on selected type
    const codeType = formData.codeType;
    if (codeType && codeType !== 'none') {
      const codeFieldMap = {
        'mermaid': { field: 'mermaidCode', lang: 'mermaid' },
        'yaml': { field: 'yamlCode', lang: 'yaml' },
        'json': { field: 'jsonCode', lang: 'json' },
        'table': { field: 'tableCode', lang: null }, // Tables don't need code fence
        'tree': { field: 'treeCode', lang: 'text' }, // Tree structure as text block
        'typescript': { field: 'typescriptCode', lang: 'typescript' },
        'javascript': { field: 'javascriptCode', lang: 'javascript' },
        'bash': { field: 'bashCode', lang: 'bash' }
      };

      const codeConfig = codeFieldMap[codeType];
      if (codeConfig && formData[codeConfig.field]?.trim()) {
        const codeContent = formData[codeConfig.field].trim();
        if (codeConfig.lang) {
          contentParts.push('\n```' + codeConfig.lang + '\n' + codeContent + '\n```');
        } else {
          // For tables, add directly without code fence
          contentParts.push('\n' + codeContent);
        }
      }
    }

    return contentParts.join('\n\n');
  },

  default: (formData) => {
    return formData.content || '';
  }
};

// pipeline-stage uses the same content generator as git-guideline
CONTENT_GENERATORS['pipeline-stage'] = CONTENT_GENERATORS['git-guideline'];

/**
 * Generate content based on section type and form data
 * @param {string} sectionType - The type of section
 * @param {Object} formData - The form data
 * @returns {string} Generated content
 */
export const generateContent = (sectionType, formData) => {
  const generator = CONTENT_GENERATORS[sectionType] || CONTENT_GENERATORS.default;
  return generator(formData);
};

/**
 * Legacy function for backward compatibility
 */
export const generateRoleContent = (formData) => {
  return generateContent('userRoles', formData);
};

/**
 * Register a new field configuration
 * @param {string} sectionType - The section type identifier
 * @param {Array} fields - Array of field configurations
 */
export const registerFieldConfig = (sectionType, fields) => {
  FIELD_CONFIGS[sectionType] = fields;
};

/**
 * Register a new content generator
 * @param {string} sectionType - The section type identifier
 * @param {Function} generator - Content generation function
 */
export const registerContentGenerator = (sectionType, generator) => {
  CONTENT_GENERATORS[sectionType] = generator;
};